import { useFlag } from "@unleash/proxy-client-react";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { MergedRecorder } from "api/interfaces/recorderInterface.interface";
import { useAppSelector } from "redux/hooks";
import * as s from "pages/recorders/RecordersStyled";
import { WithTooltip } from "components/blocks/WithTooltip";
import { isVersionGreater } from "utils/functions";
import { useCallback, useMemo } from "react";
import { GrUpgrade } from "react-icons/gr";

export default function FirmwareVersionColumn({
  firmwareUpgradeVersion16CH,
  firmwareUpgradeVersion4CH,
  autoUpdate,
  row,
  onClickUpdateFirmware,
}: {
  firmwareUpgradeVersion16CH: string;
  firmwareUpgradeVersion4CH: string;
  autoUpdate: boolean;
  row: MergedRecorder;
  onClickUpdateFirmware?: (row: MergedRecorder) => void;
}): JSX.Element {
  const isFirmwareUpgradeEnable = useFlag("feature_manage_firmwareUpgrade");
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const getVersion = useCallback(
    (model: string) => {
      return (model ?? "").includes("16CH")
        ? firmwareUpgradeVersion16CH
        : firmwareUpgradeVersion4CH ?? "";
    },
    [firmwareUpgradeVersion16CH, firmwareUpgradeVersion4CH]
  );
  const isUpdate = useMemo(() => {
    return (
      row.isUpdate &&
      selectedAccount.userPermission.manageRecordersAndGroups &&
      row.onlineStatusCount === row.recorderCount &&
      isFirmwareUpgradeEnable &&
      selectedAccount.isEnabled
    );
  }, [
    isFirmwareUpgradeEnable,
    row.isUpdate,
    row.onlineStatusCount,
    row.recorderCount,
    selectedAccount.isEnabled,
    selectedAccount.userPermission.manageRecordersAndGroups,
  ]);
  const isVersionGreaterResult = useMemo(() => {
    if (!row.firmwareVersion || !row.recorders[0]?.model) return false;
    return isVersionGreater({
      installedVersion: row.firmwareVersion,
      latestVersion: getVersion(row.recorders[0]?.model ?? ""),
    });
  }, [getVersion, row.firmwareVersion, row.recorders]);
  return isUpdate ? (
    <WithTooltip
      tooltip={
        !isVersionGreaterResult
          ? `Update required ${getVersion(
              row.recorders[0]?.model ?? ""
            )} or later`
          : autoUpdate
          ? "This firmware will be automatically updated."
          : "Available firmware update"
      }
    >
      <s.Row
        onClick={
          isVersionGreaterResult
            ? () => onClickUpdateFirmware?.(row)
            : undefined
        }
        $inline
        data-testid="maintenance-firmware-firmware-upgrade-row-click"
      >
        {row.firmwareVersion} <GrUpgrade className="firmware-icon" size={15} />
      </s.Row>
    </WithTooltip>
  ) : (
    <>{row.firmwareVersion ?? ""}</>
  );
}
